import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable, of, timer } from 'rxjs';
import { tap, map, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { REQUIRE_AUTH } from '../interceptors/auth-context';

export interface MembershipPlanFeatureDto {
  membershipPlanFeatureId: string;
  featureName: string;
  featureDescription?: string | null;
  isIncluded: boolean;
  displayOrder?: number | null;
  priceMonthly?: number | null;
  priceAnnual?: number | null;
}

export interface MembershipPlanDto {
  membershipPlanId: string;
  name: string;
  description?: string | null;
  priceMonthly: number;
  priceAnnual: number;
  annualDiscountPercentage: number;
  trialDays: number;
  isActive: boolean;
  externalPlanId?: string | null;
  pricePerProfessional?: number | null;
  numberOfProfessionals: number;
  createdAt: string;
  updatedAt?: string | null;
  features: MembershipPlanFeatureDto[];
}

export interface GetMembershipPlansResponse {
  membershipPlans: MembershipPlanDto[];
}

export interface MembershipPlanBasicDto {
  membershipPlanId: string;
  name: string;
  description?: string | null;
  priceMonthly: number;
  priceAnnual: number;
  annualDiscountPercentage: number;
  trialDays: number;
  isActive: boolean;
  externalPlanId?: string | null;
  pricePerProfessional?: number | null;
  numberOfProfessionals: number;
  includedFeatureNames: string[];
}

export interface BillingCycleDto {
  billingCycleId: string;
  name: string; // ej: "Monthly" | "Annual"
  displayName: string; // ej: "Mensual" | "Anual"
  description?: string | null;
  isActive: boolean;
}

export interface MembershipStatusDto {
  membershipStatusId: string;
  name: string; // ej: "Active"
  displayName: string; // ej: "Activa"
  description?: string | null;
  isActive: boolean;
}

export interface BusinessMembershipFeatureDto {
  businessMembershipFeatureId: string;
  membershipPlanFeatureId: string;
  featureName: string;
  quantity: number;
  pricePaid: number;
  purchaseDate: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
}

export interface BusinessMembershipDto {
  businessMembershipId: string;
  businessId: string;
  membershipPlanId: string;
  billingCycleId: string;
  membershipStatusId: string;
  startDate: string;
  endDate?: string | null;
  trialStartDate?: string | null;
  trialEndDate?: string | null;
  nextBillingDate?: string | null;
  externalMembershipId?: string | null;
  paidAmount?: number | null;
  numberOfProfessionals: number;
  paidProfessionalsCount: number;
  createdAt: string;
  updatedAt?: string | null;
  membershipPlan: MembershipPlanBasicDto;
  billingCycle: BillingCycleDto;
  membershipStatus: MembershipStatusDto;
  additionalFeatures: BusinessMembershipFeatureDto[];
  /** Last day already paid for, counting renewals paid in advance */
  paidThroughDate?: string | null;
}

export interface GetLatestBusinessMembershipResponse {
  businessMembership: BusinessMembershipDto | null;
}

export interface PaymentHistoryFeatureDto {
  featureName: string;
  pricePaid: number;
  quantity: number;
}

export interface PaymentHistoryItemDto {
  paymentHistoryId: string;
  amount: number;
  currency: string;
  description: string;
  paymentDate: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  status: string;
  statusDisplayName: string;
  receiptUrl?: string | null;
  failureReason?: string | null;
  externalPaymentId?: string | null;
  externalTransactionId?: string | null;
  planName?: string | null;
  features?: PaymentHistoryFeatureDto[];
}

export interface GetPaymentHistoryResponse {
  payments: PaymentHistoryItemDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface MembershipHistoryItemDto {
  businessMembershipId: string;
  planName: string;
  billingCycle?: string | null;
  startDate: string;
  endDate?: string | null;
  trialStartDate?: string | null;
  trialEndDate?: string | null;
  status: string;
  statusDisplayName: string;
  amountPaid: number;
  currency: string;
  numberOfProfessionals: number;
  createdAt: string;
}

export interface GetMembershipHistoryResponse {
  memberships: MembershipHistoryItemDto[];
}

@Injectable({ providedIn: 'root' })
export class MembershipService {
  private http = inject(HttpClient);
  private base = environment.apiUrl;
  private cachedMembership: BusinessMembershipDto | null = null;
  private membershipLoading = false;

  getPlans(): Observable<GetMembershipPlansResponse> {
    return this.http.get<GetMembershipPlansResponse>(
      `${this.base}/api/v1/membership/plans`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true)
      }
    );
  }

  getLatestBusinessMembership(
    businessId: string
  ): Observable<GetLatestBusinessMembershipResponse> {
    return this.http.get<GetLatestBusinessMembershipResponse>(
      `${this.base}/api/v1/membership/business/${businessId}/latest`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true)
      }
    ).pipe(
      tap(response => {
        // Cache the membership when loaded
        if (response.businessMembership) {
          this.cachedMembership = response.businessMembership;
        }
      })
    );
  }

  /**
   * Get cached latest membership or load it if not available
   */
  getCachedLatestMembership(businessId: string): Observable<BusinessMembershipDto | null> {
    if (this.cachedMembership) {
      return of(this.cachedMembership);
    }

    if (this.membershipLoading) {
      // If already loading, wait a bit and try again
      return timer(100).pipe(
        switchMap(() => this.getCachedLatestMembership(businessId))
      );
    }

    this.membershipLoading = true;
    return this.getLatestBusinessMembership(businessId).pipe(
      map(response => response.businessMembership),
      tap(() => {
        this.membershipLoading = false;
      }),
      catchError(() => {
        this.membershipLoading = false;
        return of(null);
      })
    );
  }

  /**
   * Fetch latest membership from backend, bypassing local cache.
   */
  getLatestBusinessMembershipFresh(
    businessId: string
  ): Observable<BusinessMembershipDto | null> {
    return this.getLatestBusinessMembership(businessId).pipe(
      map(response => response.businessMembership ?? null),
      catchError(() => of(null))
    );
  }

  /**
   * Get cached membership synchronously (may return null if not loaded)
   */
  getCachedMembership(): BusinessMembershipDto | null {
    return this.cachedMembership;
  }

  /**
   * Clear cached membership (useful when membership changes)
   */
  clearCache(): void {
    this.cachedMembership = null;
  }

  getPaymentHistory(page = 1, pageSize = 20): Observable<GetPaymentHistoryResponse> {
    const params = new HttpParams()
      .set('page', page)
      .set('pageSize', pageSize);

    return this.http.get<GetPaymentHistoryResponse>(
      `${this.base}/api/v1/membership/payments/history`,
      {
        params,
        context: new HttpContext().set(REQUIRE_AUTH, true)
      }
    );
  }

  getAllPaymentHistory(): Observable<GetPaymentHistoryResponse> {
    return this.http.get<GetPaymentHistoryResponse>(
      `${this.base}/api/v1/membership/payments/history/all`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true)
      }
    );
  }

  getMembershipHistory(): Observable<GetMembershipHistoryResponse> {
    return this.http.get<GetMembershipHistoryResponse>(
      `${this.base}/api/v1/membership/history`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true)
      }
    );
  }
}
